import ReactDOM from 'react-dom/client'
import React from 'react'
import { InteractionType } from '@azure/msal-browser'
import { MsalAuthenticationTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react'
import { authRequest, msalInstance } from '@/api/AuthConfig'

import App from '@/App'

import Unauthorized from '@//pages/unauthorized/unauthorized'
import '@/index.css'
import Loading from '@/components/loading/Loading'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { QueryKeys } from './core/models/enum'
import { getErrorMessage } from './core/models/errorHandling'
import { ResponseObject } from './core/models/types'
import { AxiosError } from 'axios'
import { addGlobalErrorMessage, addSnackBarMessage, globalStore } from './core/store/globalStore'
import { Provider } from 'jotai'
import Toast from './components/snackbar/Toast'
import { Realtime } from './api/Realtime'

// Backend denies plant access with a 403 ProblemDetails describing which access group to apply for.
function getForbiddenMessage(error: unknown): string | null {
    const response = (error as AxiosError<{ detail?: string; message?: string }>)?.response
    if (response?.status !== 403) return null

    return response.data?.detail ?? response.data?.message ?? 'You do not have access to this resource.'
}

const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => {
            const forbiddenMessage = getForbiddenMessage(error)
            if (forbiddenMessage) {
                addGlobalErrorMessage(forbiddenMessage)
                return
            }

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore

            // capture fetch requests.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (error?.code === 'ERR_BAD_REQUEST' && !error?.response?.data?.message) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const mapping: Record<QueryKeys, string> = {
                    [QueryKeys.OnboardedSensors]: 'onboarded sensors',
                    [QueryKeys.Plants]: 'plants',
                }
                const text = mapping[query.queryKey[0] as QueryKeys] ?? query.queryKey[0]
                const errorMessage = getErrorMessage(error as AxiosError<ResponseObject>, `Failed to load ${text}`)
                addSnackBarMessage(errorMessage.displayError)
            }
        },
    }),
})

// TODO: Revisit to see what can be done about index.css

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Failed to find the root element')
const root = ReactDOM.createRoot(rootElement!)

root.render(
    <React.StrictMode>
        <MsalProvider instance={msalInstance}>
            <QueryClientProvider client={queryClient}>
                <Provider store={globalStore}>
                    <UnauthenticatedTemplate>
                        <div className="sign-in-page">
                            <Unauthorized />
                        </div>
                    </UnauthenticatedTemplate>
                    <MsalAuthenticationTemplate
                        interactionType={InteractionType.Redirect}
                        authenticationRequest={authRequest}
                        loadingComponent={Loading}
                    >
                        <Realtime queryClient={queryClient} />
                        <App strategy={'local'} />
                        <Toast></Toast>
                    </MsalAuthenticationTemplate>
                </Provider>
            </QueryClientProvider>
        </MsalProvider>
    </React.StrictMode>
)
